import { Button, Stack } from "@mantine/core";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import type { WizardMissingData } from "../../../utils/backend-types";
import { StepStatus } from "../ChangeProcess.types";
import { MarkDoneActionButton } from "./MarkDoneActionButton";
import { MissingDataActionButton } from "./MissingDataActionButton";
import { SiteTaskPhase } from "./SiteTaskPhase";

interface StepShellProps {
  label: string;
  status: StepStatus;
  highlight?: boolean;
  children: ReactNode;
}

function StepShell({ label, status, highlight, children }: StepShellProps) {
  return (
    <SiteTaskPhase highlight={highlight} label={label} status={status}>
      <Stack gap="xs" mt="xs">
        {children}
      </Stack>
    </SiteTaskPhase>
  );
}

interface SkipButtonProps {
  disabled: boolean;
  onClick: () => void;
}

function SkipButton({ disabled, onClick }: SkipButtonProps) {
  return (
    <Button disabled={disabled} variant="default" onClick={onClick}>
      Überspringen
    </Button>
  );
}

interface StepWithMissingDataProps {
  label: string;
  status: StepStatus;
  disabled?: boolean;
  hideSkip?: boolean;
  missingData: Array<WizardMissingData> | null;
  isUpdating: boolean;
  onUpdateStepStatus: (status: StepStatus) => void;
  modalLink: string;
}

function StepWithMissingData({
  label,
  status,
  missingData,
  isUpdating,
  disabled = false,
  hideSkip = false,
  onUpdateStepStatus,
  modalLink
}: StepWithMissingDataProps) {
  const isBlocked = missingData !== null && missingData.length > 0;
  const actionDisabled = isUpdating || disabled;

  return (
    <StepShell label={label} status={status}>
      {status === StepStatus.open && !isBlocked && (
        <MarkDoneActionButton
          disabled={actionDisabled}
          onClick={() => onUpdateStepStatus(StepStatus.done)}
        />
      )}
      {status === StepStatus.open && isBlocked && (
        <>
          <MissingDataActionButton
            disabled={actionDisabled}
            route={modalLink}
          />
          {!hideSkip && (
            <SkipButton
              disabled={actionDisabled}
              onClick={() => onUpdateStepStatus(StepStatus.notRelevant)}
            />
          )}
        </>
      )}
    </StepShell>
  );
}

interface StepNewContractProps {
  status: StepStatus;
  disabled?: boolean;
  isUpdating: boolean;
  objectsAreMissing: boolean | null;
  onUpdateStepStatus: (status: StepStatus) => void;
  newContractLink?: string;
}

function StepNewContract({
  status,
  isUpdating,
  disabled = false,
  objectsAreMissing,
  onUpdateStepStatus,
  newContractLink
}: StepNewContractProps) {
  const navigate = useNavigate();
  const actionDisabled = isUpdating || disabled;

  return (
    <StepShell label="Neue Verträge & Lieferungen anlegen" status={status}>
      {status === StepStatus.open && !objectsAreMissing && (
        <MarkDoneActionButton
          disabled={actionDisabled}
          onClick={() => onUpdateStepStatus(StepStatus.done)}
        />
      )}
      {status === StepStatus.open && objectsAreMissing && (
        <>
          {newContractLink && (
            <Button
              disabled={actionDisabled}
              onClick={() => navigate(newContractLink)}
            >
              Neue Verträge und Lieferungen anlegen
            </Button>
          )}
          <SkipButton
            disabled={actionDisabled}
            onClick={() => onUpdateStepStatus(StepStatus.notRelevant)}
          />
        </>
      )}
    </StepShell>
  );
}

interface StepStaffReviewOnlyProps {
  label: string;
  status: StepStatus;
  disabled?: boolean;
  isUpdating: boolean;
  shouldShowStaffView: boolean;
  onUpdateStepStatus: (status: StepStatus) => void;
}

function StepStaffReviewOnly({
  label,
  status,
  isUpdating,
  disabled = false,
  shouldShowStaffView,
  onUpdateStepStatus
}: StepStaffReviewOnlyProps) {
  const actionDisabled = isUpdating || disabled;

  return (
    <StepShell
      highlight={status === StepStatus.open && !shouldShowStaffView}
      label={label}
      status={status}
    >
      {status === StepStatus.open && shouldShowStaffView && (
        <MarkDoneActionButton
          disabled={actionDisabled}
          onClick={() => onUpdateStepStatus(StepStatus.done)}
        />
      )}
      {status === StepStatus.open && !shouldShowStaffView && (
        <span>Prüfung durch Mitarbeiter:innen</span>
      )}
    </StepShell>
  );
}

export {
  StepNewContract,
  StepStaffReviewOnly,
  StepWithMissingData,
  type StepNewContractProps,
  type StepStaffReviewOnlyProps,
  type StepWithMissingDataProps
};
